import { darken, getContrastRatio } from '@mui/material/styles';

/** Apariencia por empresa: se guarda en Configuraciones del tenant (usuario global ''). */
export const APARIENCIA_SECCION = 'APARIENCIA';
export const APARIENCIA_CAMPO_COLOR = 'COLOR_ACENTO';
export const APARIENCIA_CAMPO_ETIQUETA = 'ETIQUETA';
export const APARIENCIA_CAMPO_AVISO = 'MOSTRAR_AVISO';
export const APARIENCIA_CAMPO_AVISO_OPACIDAD = 'AVISO_OPACIDAD';
export const APARIENCIA_CAMPO_AVISO_POSICION = 'AVISO_POSICION';

export const APARIENCIA_ETIQUETA_MAX = 20;

/** 100 = sólida. Por debajo se ve la pantalla detrás de la cinta. */
export const AVISO_OPACIDAD_MIN = 30;
export const AVISO_OPACIDAD_MAX = 100;
export const AVISO_OPACIDAD_DEFAULT = 100;

/** Alto del AppBar en unidades de spacing (64px) y de la franja (32px). */
export const APP_HEADER_OFFSET_SPACING = 8;
export const AVISO_FRANJA_OFFSET_SPACING = 4;

export const AVISO_POSICIONES = [
  'franja',
  'franja-inf',
  'esquina-sup-izq',
  'esquina-sup-der',
  'esquina-inf-izq',
  'esquina-inf-der',
] as const;

export type AvisoPosicion = (typeof AVISO_POSICIONES)[number];

export const AVISO_POSICION_DEFAULT: AvisoPosicion = 'franja';

export const AVISO_POSICION_OPCIONES: { id: AvisoPosicion; label: string; descripcion: string }[] = [
  {
    id: 'franja',
    label: 'Franja superior',
    descripcion: 'Barra bajo el encabezado. Reserva su lugar y no tapa la pantalla.',
  },
  {
    id: 'franja-inf',
    label: 'Franja inferior',
    descripcion: 'Barra fija al pie de la pantalla. Reserva su lugar y no tapa el contenido.',
  },
  {
    id: 'esquina-sup-izq',
    label: 'Sup. izquierda',
    descripcion: 'Cinta diagonal en el vértice superior izquierdo.',
  },
  {
    id: 'esquina-sup-der',
    label: 'Sup. derecha',
    descripcion: 'Cinta diagonal en el vértice superior derecho.',
  },
  {
    id: 'esquina-inf-izq',
    label: 'Inf. izquierda',
    descripcion: 'Cinta diagonal en el vértice inferior izquierdo.',
  },
  {
    id: 'esquina-inf-der',
    label: 'Inf. derecha',
    descripcion: 'Cinta diagonal en el vértice inferior derecho.',
  },
];

export interface TenantAppearance {
  colorAcento: string | null;
  etiqueta: string | null;
  mostrarAviso: boolean;
  avisoOpacidad: number;
  avisoPosicion: AvisoPosicion;
}

export const EMPTY_TENANT_APPEARANCE: TenantAppearance = {
  colorAcento: null,
  etiqueta: null,
  mostrarAviso: false,
  avisoOpacidad: AVISO_OPACIDAD_DEFAULT,
  avisoPosicion: AVISO_POSICION_DEFAULT,
};

/** Margen superior del contenido y del menú: el encabezado, más la franja superior si está visible. */
export function appHeaderOffsetSpacing(avisoVisible: boolean, posicion: AvisoPosicion): number {
  return (
    APP_HEADER_OFFSET_SPACING +
    (avisoVisible && posicion === 'franja' ? AVISO_FRANJA_OFFSET_SPACING : 0)
  );
}

/** La franja inferior queda fija al pie y el contenido reserva ese alto. */
export function avisoReservaPie(avisoVisible: boolean, posicion: AvisoPosicion): boolean {
  return avisoVisible && posicion === 'franja-inf';
}

export interface AppearancePreset {
  id: string;
  label: string;
  description: string;
  appearance: TenantAppearance;
}

export const APPEARANCE_PRESETS: AppearancePreset[] = [
  {
    id: 'principal',
    label: 'Principal',
    description: 'Colores estándar, sin etiqueta ni aviso.',
    appearance: EMPTY_TENANT_APPEARANCE,
  },
  {
    id: 'secundario',
    label: 'Secundario',
    description: 'Ámbar, etiqueta SECUNDARIO y franja de aviso.',
    appearance: {
      colorAcento: '#d97706',
      etiqueta: 'SECUNDARIO',
      mostrarAviso: true,
      avisoOpacidad: AVISO_OPACIDAD_DEFAULT,
      avisoPosicion: AVISO_POSICION_DEFAULT,
    },
  },
];

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

export function normalizeHexColor(value: string | null | undefined): string | null {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const withHash = raw.startsWith('#') ? raw : `#${raw}`;
  return HEX_COLOR_RE.test(withHash) ? withHash.toLowerCase() : null;
}

export function normalizeEtiqueta(value: string | null | undefined): string | null {
  const raw = String(value ?? '').trim();
  return raw ? raw.slice(0, APARIENCIA_ETIQUETA_MAX) : null;
}

export function normalizeAvisoOpacidad(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  if (!Number.isFinite(parsed)) return AVISO_OPACIDAD_DEFAULT;
  return Math.min(AVISO_OPACIDAD_MAX, Math.max(AVISO_OPACIDAD_MIN, Math.round(parsed)));
}

export function normalizeAvisoPosicion(value: unknown): AvisoPosicion {
  const raw = String(value ?? '').trim();
  return (AVISO_POSICIONES as readonly string[]).includes(raw) ? (raw as AvisoPosicion) : AVISO_POSICION_DEFAULT;
}

export function normalizeAppearance(raw: Partial<TenantAppearance> | null | undefined): TenantAppearance {
  return {
    colorAcento: normalizeHexColor(raw?.colorAcento),
    etiqueta: normalizeEtiqueta(raw?.etiqueta),
    mostrarAviso: Boolean(raw?.mostrarAviso),
    avisoOpacidad: normalizeAvisoOpacidad(raw?.avisoOpacidad),
    avisoPosicion: normalizeAvisoPosicion(raw?.avisoPosicion),
  };
}

export function isAppearanceEmpty(appearance: TenantAppearance): boolean {
  return !appearance.colorAcento && !appearance.etiqueta && !appearance.mostrarAviso;
}

export function sameAppearance(a: TenantAppearance, b: TenantAppearance): boolean {
  return (
    a.colorAcento === b.colorAcento &&
    a.etiqueta === b.etiqueta &&
    a.mostrarAviso === b.mostrarAviso &&
    a.avisoOpacidad === b.avisoOpacidad &&
    a.avisoPosicion === b.avisoPosicion
  );
}

/** Texto legible (blanco o casi negro) sobre el color dado, según luminancia WCAG. */
export function contrastTextColor(hex: string): '#ffffff' | '#111827' {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return '#111827';
  const channel = (start: number) => {
    const c = parseInt(normalized.slice(start, start + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  const luminance = 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
  return luminance > 0.179 ? '#111827' : '#ffffff';
}

const MIN_INK_CONTRAST = 3;

/**
 * Variante del color usable como texto/borde sobre fondo blanco.
 * Colores claros (blanco, amarillos, pasteles) se oscurecen hasta ser legibles.
 */
export function accentInk(color: string): string {
  const normalized = normalizeHexColor(color);
  if (!normalized) return color;
  let ink = normalized;
  for (let step = 1; step <= 10 && getContrastRatio(ink, '#ffffff') < MIN_INK_CONTRAST; step += 1) {
    ink = darken(normalized, step * 0.1);
  }
  return ink;
}

const CACHE_PREFIX = 'tenantAppearance:';

export function appearanceCacheKey(tenantId: number): string {
  return `${CACHE_PREFIX}${tenantId}`;
}

export function parseCachedAppearance(raw: string | null): TenantAppearance | null {
  if (!raw) return null;
  try {
    return normalizeAppearance(JSON.parse(raw) as Partial<TenantAppearance>);
  } catch {
    return null;
  }
}

export function readCachedAppearance(tenantId: number): TenantAppearance | null {
  try {
    return parseCachedAppearance(localStorage.getItem(appearanceCacheKey(tenantId)));
  } catch {
    return null;
  }
}

export function writeCachedAppearance(tenantId: number, appearance: TenantAppearance): void {
  try {
    if (isAppearanceEmpty(appearance)) {
      localStorage.removeItem(appearanceCacheKey(tenantId));
    } else {
      localStorage.setItem(appearanceCacheKey(tenantId), JSON.stringify(appearance));
    }
  } catch {
    // localStorage no disponible (modo privado / cuota): la apariencia queda solo en memoria.
  }
}

/** Colores configurados conocidos en este navegador para el set de empresas del usuario. */
export function readCachedAccentMap(tenantIds: number[]): Record<number, string> {
  const map: Record<number, string> = {};
  tenantIds.forEach((id) => {
    const color = readCachedAppearance(id)?.colorAcento;
    if (color) map[id] = color;
  });
  return map;
}

export function tenantIdFromCacheKey(key: string | null): number | null {
  if (!key || !key.startsWith(CACHE_PREFIX)) return null;
  const id = Number(key.slice(CACHE_PREFIX.length));
  return Number.isFinite(id) ? id : null;
}
